import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function FightOutcomeVisualizer() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [hoveredWindow, setHoveredWindow] = useState(null);
    const [hoveredOutcome, setHoveredOutcome] = useState(null);
    const hoverTimeoutRef = React.useRef(null);
    const [weightClasses, setWeightClasses] = useState([]);
    const [selectedWeightClasses, setSelectedWeightClasses] = useState([]);
    const [selectedOutcomes, setSelectedOutcomes] = useState(['Decision', '(T)KO', 'Submission']);
    const [outcomeFrequencies, setOutcomeFrequencies] = useState([]);
    const [frequenciesLoading, setFrequenciesLoading] = useState(true);
    const [expandedOutcome, setExpandedOutcome] = useState(null);
    const [expandedFights, setExpandedFights] = useState([]);
    const [expandedFightsLoading, setExpandedFightsLoading] = useState(false);
    const [showAllDivisionsCombined, setShowAllDivisionsCombined] = useState(true);
    const [winnerRankThreshold, setWinnerRankThreshold] = useState('');
    const [rankThresholdInput, setRankThresholdInput] = useState('');
    const [windowYears, setWindowYears] = useState('1');
    const [windowMonths, setWindowMonths] = useState('0');
    const [windowYearsInput, setWindowYearsInput] = useState('1');
    const [windowMonthsInput, setWindowMonthsInput] = useState('0');
    const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
    const fightsCache = React.useRef({});

    const allOutcomes = ['Decision', '(T)KO', 'Submission'];
    
    // Map display names to database names
    const outcomeToDbName = {
        'Decision': 'Decision',
        '(T)KO': 'KO/TKO',
        'Submission': 'Submission'
    };
    
    // Map database names to display names
    const dbNameToOutcome = {
        'Decision': 'Decision',
        'KO/TKO': '(T)KO',
        'Submission': 'Submission'
    };

    // Organize weight classes by gender and weight
    const maleWeightClasses = ['FLW', 'BW', 'FW', 'LW', 'WW', 'MW', 'LHW', 'HW'];
    const femaleWeightClasses = ['W115', 'W125', 'W135'];

    useEffect(() => {
        fetchWeightClasses();
        fetchOutcomeFrequencies();

        // Cleanup timeout on unmount
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        fetchOutcomeData();
    }, [selectedWeightClasses, showAllDivisionsCombined, winnerRankThreshold, windowYears, windowMonths]);

    const fetchWeightClasses = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/weight-classes`);
            if (!res.ok) throw new Error('Failed to fetch weight classes');
            const result = await res.json();
            setWeightClasses(result);
        } catch (e) {
            console.error('Error fetching weight classes:', e.message);
        }
    };

    const fetchOutcomeFrequencies = async () => {
        try {
            const res = await fetch(`${API_BASE}/api/fight-outcome-frequencies`);
            if (!res.ok) throw new Error('Failed to fetch outcome frequencies');
            const result = await res.json();
            setOutcomeFrequencies(result);
            setFrequenciesLoading(false);
        } catch (e) {
            console.error('Error fetching outcome frequencies:', e.message);
            setFrequenciesLoading(false);
        }
    };

    const fetchFightsByOutcome = async (outcomeType) => {
        // Check cache first
        if (fightsCache.current[outcomeType]) {
            setExpandedFights(fightsCache.current[outcomeType]);
            return;
        }

        setExpandedFightsLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/fights-by-outcome?outcome_type=${encodeURIComponent(outcomeType)}`);
            if (!res.ok) throw new Error('Failed to fetch fights');
            const result = await res.json();
            fightsCache.current[outcomeType] = result;
            setExpandedFights(result);
        } catch (e) {
            console.error('Error fetching fights by outcome:', e.message);
            setExpandedFights([]);
        } finally {
            setExpandedFightsLoading(false);
        }
    };

    const toggleOutcomeExpand = (outcomeType) => {
        if (expandedOutcome === outcomeType) {
            setExpandedOutcome(null);
        } else {
            setExpandedOutcome(outcomeType);
            fetchFightsByOutcome(outcomeType);
        }
    };

    const fetchOutcomeData = async () => {
        // If showing all divisions combined, fetch without weight class filter
        if (showAllDivisionsCombined) {
            setLoading(true);
            setError('');
            try {
                const params = new URLSearchParams();
                if (winnerRankThreshold) params.set('rank_threshold', winnerRankThreshold);
                params.set('window_years', windowYears);
                params.set('window_months', windowMonths);
                const res = await fetch(`${API_BASE}/api/outcome-by-year?${params.toString()}`);
                if (!res.ok) throw new Error('Failed to fetch combined division data');
                const result = await res.json();
                // Add a special weight_class marker for combined data
                const processedData = result.map(row => ({ ...row, weight_class: 'ALL' }));
                setData(processedData);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
            return;
        }

        // If no weight classes selected, just clear the data without showing error
        if (selectedWeightClasses.length === 0) {
            setData([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError('');
        try {
            // Fetch data for all selected weight classes in parallel
            const promises = selectedWeightClasses.map(wc => {
                const params = new URLSearchParams({ weight_class: wc });
                if (winnerRankThreshold) params.set('rank_threshold', winnerRankThreshold);
                params.set('window_years', windowYears);
                params.set('window_months', windowMonths);
                return fetch(`${API_BASE}/api/outcome-by-year?${params.toString()}`)
                    .then(res => {
                        if (!res.ok) throw new Error(`Failed to fetch data for ${wc}`);
                        return res.json();
                    })
                    .then(data => data.map(row => ({ ...row, weight_class: wc })));
            });
            
            const results = await Promise.all(promises);
            const combined = results.flat();
            setData(combined);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    // Process data for visualization
    const { months, outcomeWeightGroups, chartData } = useMemo(() => {
        if (!data || data.length === 0) return { months: [], outcomeWeightGroups: [], chartData: {} };

        const filteredData = data;

        // Get unique months and outcome-weight class combinations, filtering by selected outcomes
        const monthsSet = new Set();
        const groupsSet = new Set();

        filteredData.forEach(row => {
            // Convert database outcome name to display name
            const displayOutcome = dbNameToOutcome[row.outcome_group] || row.outcome_group;

            // Only include data for selected outcomes
            if (!selectedOutcomes.includes(displayOutcome)) {
                return;
            }

            monthsSet.add(row.year);
            // Create unique identifier for outcome + weight class
            const groupKey = `${displayOutcome}|${row.weight_class}`;
            groupsSet.add(groupKey);
        });

        const sortedMonths = Array.from(monthsSet).sort();
        const sortedGroups = Array.from(groupsSet).sort();

        // Create a map: month (YYYY-MM) -> groupKey (outcome|weight_class) -> { proportion, count, outcome_group, weight_class }
        const monthData = {};
        filteredData.forEach(row => {
            // Convert database outcome name to display name
            const displayOutcome = dbNameToOutcome[row.outcome_group] || row.outcome_group;
            
            // Only include data for selected outcomes
            if (!selectedOutcomes.includes(displayOutcome)) {
                return;
            }
            
            if (!monthData[row.year]) monthData[row.year] = {};
            const groupKey = `${displayOutcome}|${row.weight_class}`;
            monthData[row.year][groupKey] = {
                proportion: row.proportion,
                count: row.count || 0,
                total_in_window: row.total_in_window || 0,
                outcome_group: displayOutcome,
                weight_class: row.weight_class
            };
        });

        return {
            months: sortedMonths,
            outcomeWeightGroups: sortedGroups,
            chartData: monthData
        };
    }, [data, selectedOutcomes]);

    // Color palette for outcome groups (outcome type determines color)
    const getOutcomeColor = (outcomeGroup) => {
        const colors = {
            'Decision': '#1E90FF',      // Blue
            '(T)KO': '#FF4444',       // Red
            'Submission': '#FF8C00',   // Orange
            'null': '#9D4EDD'          // Purple
        };
        return colors[outcomeGroup] || '#A78BFA'; // Purple as fallback
    };

    // Get color for a specific weight class with gradient based on weight
    const getWeightClassColor = (outcomeGroup, weightClass) => {
        const baseColor = getOutcomeColor(outcomeGroup);
        
        // If showing all divisions combined, use the base outcome color without gradient
        if (weightClass === 'ALL') return baseColor;
        
        // Weight class ordering from lightest to heaviest
        const allWeights = ['W115', 'W125', 'W135', 'FLW', 'BW', 'FW', 'LW', 'WW', 'MW', 'LHW', 'HW'];
        const weightIndex = allWeights.indexOf(weightClass);
        
        if (weightIndex === -1) return baseColor;
        
        // Calculate intensity (0 = lightest, 1 = darkest)
        const intensity = weightIndex / (allWeights.length - 1);
        
        // Create a gradient from a medium shade (50% intensity) to the base color
        // This prevents the lightest end from being too washed out
        const rgb = hexToRgb(baseColor);
        if (!rgb) return baseColor;
        
        // Start from 50% lightened version (instead of 100% lightened/white)
        const midpoint = {
            r: Math.round(rgb.r + (255 - rgb.r) * 0.5),
            g: Math.round(rgb.g + (255 - rgb.g) * 0.5),
            b: Math.round(rgb.b + (255 - rgb.b) * 0.5)
        };
        
        // Interpolate between midpoint (at intensity=0) and base color (at intensity=1)
        const adjusted = {
            r: Math.round(midpoint.r + (rgb.r - midpoint.r) * intensity),
            g: Math.round(midpoint.g + (rgb.g - midpoint.g) * intensity),
            b: Math.round(midpoint.b + (rgb.b - midpoint.b) * intensity)
        };
        
        return rgbToHex(adjusted.r, adjusted.g, adjusted.b);
    };

    // Helper function to convert hex to RGB
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };

    // Helper function to convert RGB to hex
    const rgbToHex = (r, g, b) => {
        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    };

    // Line style based on weight class to distinguish multiple weight classes
    const getLineStyle = (weightClass) => {
        const styles = {
            'LW': { strokeDasharray: null, strokeWidth: '2' },           // Light Weight - solid, thick
            'WW': { strokeDasharray: null, strokeWidth: '1.5' },        // Welterweight - solid
            'MW': { strokeDasharray: null, strokeWidth: '1.5' },        // Middleweight - solid
            'HW': { strokeDasharray: null, strokeWidth: '1.5' },       // Heavyweight - solid
            'LHW': { strokeDasharray: null, strokeWidth: '1.5' },   // Light Heavyweight - solid
            'FW': { strokeDasharray: null, strokeWidth: '1.5' },        // Featherweight - solid
            'BW': { strokeDasharray: null, strokeWidth: '1.5' },   // Bantamweight - solid
            'FLW': { strokeDasharray: null, strokeWidth: '1.5' },   // Flyweight - solid
            'W115': { strokeDasharray: null, strokeWidth: '1.5' },  // Women 115 - solid
            'W125': { strokeDasharray: null, strokeWidth: '1.5' },  // Women 125 - solid
            'W135': { strokeDasharray: null, strokeWidth: '1.5' },      // Women 135 - solid
            'W145': { strokeDasharray: null, strokeWidth: '1.5' }       // Women 145 - solid
        };
        return styles[weightClass] || { strokeDasharray: null, strokeWidth: '1' };
    };

    // Helper to format the rolling window length for display
    const formatWindowLabel = (years, months) => {
        const y = parseInt(years) || 0;
        const m = parseInt(months) || 0;
        const parts = [];
        if (y > 0) parts.push(`${y}-year`);
        if (m > 0) parts.push(`${m}-month`);
        if (parts.length === 0) return '0-month';
        return parts.join(' ');
    };

    // Helper to parse groupKey and get label
    const parseGroupKey = (groupKey) => {
        const [outcome, weightClass] = groupKey.split('|');
        return { outcome, weightClass };
    };

    const getGroupLabel = (groupKey) => {
        const { outcome, weightClass } = parseGroupKey(groupKey);
        if (weightClass === 'ALL') {
            return `${outcome === 'null' ? 'null' : outcome} (All)`;
        }
        return `${outcome === 'null' ? 'null' : outcome} (${weightClass})`;
    };

    // Helper function to set hovered window with debounce on clear
    const setHoveredWindowWithDebounce = (month) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        setHoveredWindow(month);
    };

    const clearHoveredWindowWithDebounce = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredWindow(null);
        }, 200); // 200ms delay before hiding tooltip
    };

    // Chart dimensions and padding
    const width = 900;
    const height = 520;
    const padding = { top: 40, right: 120, bottom: 60, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Scale functions
    const getX = (monthIndex) => {
        if (months.length === 0) return 0;
        return padding.left + (monthIndex / (months.length - 1 || 1)) * chartWidth;
    };

    const getY = (proportion) => {
        return padding.top + chartHeight - (proportion * chartHeight);
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <h1 style={styles.title}>Fight Outcome Visualizer</h1>
                <div style={styles.loading}>Loading data...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <h1 style={styles.title}>Fight Outcome Visualizer</h1>
                <div style={styles.error}>Error: {error}</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.chartContainer}>
                <div style={styles.headerContainer}>
                    <div>
                        <h1 style={styles.title}>UFC Decision & Finish Rates</h1>
                        <p style={styles.windowInfo}>
                            {formatWindowLabel(windowYears, windowMonths)} rolling window
                        </p>
                    </div>
                    <div style={styles.filtersWrapper}>
                        <div style={styles.filterContainer}>
                            <div style={styles.filterLabelWithButton}>
                                <label style={styles.filterLabel}>Outcomes</label>
                                <button
                                    onClick={() => setSelectedOutcomes([])}
                                    style={styles.clearButtonPlainText}
                                >
                                    Clear All
                                </button>
                            </div>
                            <div style={styles.multiSelectContainer}>
                                {allOutcomes.map((outcome) => (
                                    <label key={outcome} style={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={selectedOutcomes.includes(outcome)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedOutcomes([...selectedOutcomes, outcome]);
                                                } else {
                                                    setSelectedOutcomes(selectedOutcomes.filter(o => o !== outcome));
                                                }
                                            }}
                                            style={styles.checkbox}
                                        />
                                        {outcome}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div style={styles.filterContainer}>
                            <div style={styles.filterLabelWithButton}>
                                <label style={styles.filterLabel}>Weight Classes</label>
                                <button
                                    onClick={() => {
                                        setSelectedWeightClasses([]);
                                        setShowAllDivisionsCombined(false);
                                    }}
                                    style={styles.clearButtonPlainText}
                                >
                                    Clear All
                                </button>
                            </div>
                            <div style={styles.multiSelectContainerHorizontal}>
                                <div style={styles.weightClassGroupMale}>
                                    <label key="all-divisions" style={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={showAllDivisionsCombined}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setShowAllDivisionsCombined(true);
                                                    setSelectedWeightClasses([]);
                                                } else {
                                                    setShowAllDivisionsCombined(false);
                                                }
                                            }}
                                            style={styles.checkbox}
                                        />
                                        All Divisions
                                    </label>
                                    <div style={styles.divider}></div>
                                    <div style={styles.weightClassGroupLabel}>Male</div>
                                    {maleWeightClasses.filter(wc => weightClasses.includes(wc)).map((wc) => (
                                        <label key={wc} style={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                checked={selectedWeightClasses.includes(wc)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedWeightClasses([...selectedWeightClasses, wc]);
                                                        setShowAllDivisionsCombined(false);
                                                    } else {
                                                        setSelectedWeightClasses(selectedWeightClasses.filter(w => w !== wc));
                                                    }
                                                }}
                                                style={styles.checkbox}
                                            />
                                            {wc}
                                        </label>
                                    ))}
                                </div>
                                <div style={styles.weightClassGroup}>
                                    <div style={styles.weightClassGroupLabel}>Female</div>
                                    {femaleWeightClasses.filter(wc => weightClasses.includes(wc)).map((wc) => (
                                        <label key={wc} style={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                checked={selectedWeightClasses.includes(wc)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedWeightClasses([...selectedWeightClasses, wc]);
                                                        setShowAllDivisionsCombined(false);
                                                    } else {
                                                        setSelectedWeightClasses(selectedWeightClasses.filter(w => w !== wc));
                                                    }
                                                }}
                                                style={styles.checkbox}
                                            />
                                            {wc}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div style={styles.filterContainer}>
                            <div style={styles.filterLabelWithButton}>
                                <label style={styles.filterLabel}>Winner Ranked Top</label>
                                <button
                                    onClick={() => {
                                        setRankThresholdInput('');
                                        setWinnerRankThreshold('');
                                    }}
                                    style={styles.clearButtonPlainText}
                                >
                                    Clear
                                </button>
                            </div>
                            <div style={styles.rankThresholdRow}>
                                <input
                                    type="number"
                                    min="1"
                                    placeholder="e.g. 10"
                                    value={rankThresholdInput}
                                    onChange={(e) => setRankThresholdInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') setWinnerRankThreshold(rankThresholdInput);
                                    }}
                                    style={{ ...styles.filterSelect, width: '45px', minWidth: '45px', padding: '6px 8px', cursor: 'text' }}
                                />
                                <button
                                    onClick={() => setWinnerRankThreshold(rankThresholdInput)}
                                    disabled={rankThresholdInput === winnerRankThreshold}
                                    style={{
                                        ...styles.applyButton,
                                        ...(rankThresholdInput === winnerRankThreshold ? styles.applyButtonDisabled : {})
                                    }}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                        <div style={styles.filterContainer}>
                            <div style={styles.filterLabelWithButton}>
                                <label style={styles.filterLabel}>Rolling Window</label>
                                <button
                                    onClick={() => {
                                        setWindowYearsInput('1');
                                        setWindowMonthsInput('0');
                                        setWindowYears('1');
                                        setWindowMonths('0');
                                    }}
                                    style={styles.clearButtonPlainText}
                                >
                                    Reset
                                </button>
                            </div>
                            <div style={styles.rankThresholdRow}>
                                <div style={styles.windowInputGroup}>
                                    <input
                                        type="number"
                                        min="0"
                                        value={windowYearsInput}
                                        onChange={(e) => setWindowYearsInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                setWindowYears(windowYearsInput);
                                                setWindowMonths(windowMonthsInput);
                                            }
                                        }}
                                        style={{ ...styles.filterSelect, width: '35px', minWidth: '35px', padding: '6px 8px', cursor: 'text' }}
                                    />
                                    <span style={styles.windowInputUnit}>years</span>
                                </div>
                                <div style={styles.windowInputGroup}>
                                    <input
                                        type="number"
                                        min="0"
                                        max="11"
                                        value={windowMonthsInput}
                                        onChange={(e) => setWindowMonthsInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                setWindowYears(windowYearsInput);
                                                setWindowMonths(windowMonthsInput);
                                            }
                                        }}
                                        style={{ ...styles.filterSelect, width: '35px', minWidth: '35px', padding: '6px 8px', cursor: 'text' }}
                                    />
                                    <span style={styles.windowInputUnit}>months</span>
                                </div>
                                <button
                                    onClick={() => {
                                        setWindowYears(windowYearsInput);
                                        setWindowMonths(windowMonthsInput);
                                    }}
                                    disabled={windowYearsInput === windowYears && windowMonthsInput === windowMonths}
                                    style={{
                                        ...styles.applyButton,
                                        ...((windowYearsInput === windowYears && windowMonthsInput === windowMonths) ? styles.applyButtonDisabled : {})
                                    }}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <svg width={width} height={height} style={styles.svg} onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                }}>
                    {/* Grid lines */}
                    <g>
                        {[0, 0.25, 0.5, 0.75, 1.0].map(proportion => (
                            <g key={proportion}>
                                <line
                                    x1={padding.left}
                                    y1={getY(proportion)}
                                    x2={width - padding.right}
                                    y2={getY(proportion)}
                                    stroke="#444"
                                    strokeWidth="1"
                                    strokeDasharray="4 4"
                                />
                                <text
                                    x={padding.left - 10}
                                    y={getY(proportion) + 5}
                                    textAnchor="end"
                                    fill="#ccc"
                                    fontSize="12"
                                >
                                    {(proportion * 100).toFixed(0)}%
                                </text>
                            </g>
                        ))}
                    </g>

                    {/* X-axis labels (show every 5 years, Jan quarters only) */}
                    <g>
                        {months.filter(month => month.endsWith('-01-01')).filter((month, idx) => {
                            const year = parseInt(month.substring(0, 4));
                            return year % 5 === 0;
                        }).map((month) => {
                            const actualIdx = months.indexOf(month);
                            const year = month.substring(0, 4);
                            return (
                                <text
                                    key={month}
                                    x={getX(actualIdx)}
                                    y={height - padding.bottom + 20}
                                    textAnchor="middle"
                                    fill="#ccc"
                                    fontSize="12"
                                >
                                    {year}
                                </text>
                            );
                        })}
                    </g>

                    {/* X-axis tick marks (every 5 years, Jan quarters only) */}
                    <g>
                        {months.filter(month => month.endsWith('-01-01')).filter((month) => {
                            const year = parseInt(month.substring(0, 4));
                            return year % 5 === 0;
                        }).map((month) => {
                            const idx = months.indexOf(month);
                            return (
                                <line
                                    key={`tick-${month}`}
                                    x1={getX(idx)}
                                    y1={height - padding.bottom}
                                    x2={getX(idx)}
                                    y2={height - padding.bottom + 5}
                                    stroke="#ccc"
                                    strokeWidth="1.5"
                                />
                            );
                        })}
                    </g>

                    {/* Lines for each outcome-weight class combination */}
                    {outcomeWeightGroups.map((groupKey) => {
                        const { outcome, weightClass } = parseGroupKey(groupKey);
                        const color = getWeightClassColor(outcome, weightClass);
                        const lineStyle = getLineStyle(weightClass);
                        
                        const points = months
                            .map((month, idx) => {
                                const data = chartData[month]?.[groupKey];
                                const proportion = data?.proportion || 0;
                                return { month, idx, proportion, x: getX(idx), y: getY(proportion) };
                            })
                            .filter(p => p.proportion > 0);

                        if (points.length === 0) return null;

                        const pathData = points
                            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
                            .join(' ');

                        const lastPoint = points[points.length - 1];
                        
                        // Determine opacity based on hover state
                        const isHighlighted = hoveredOutcome === null || hoveredOutcome === groupKey;
                        const opacity = isHighlighted ? 1 : 0.2;

                        return (
                            <g key={groupKey}>
                                {/* Line */}
                                <path
                                    d={pathData}
                                    stroke={color}
                                    strokeWidth={lineStyle.strokeWidth}
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeDasharray={lineStyle.strokeDasharray}
                                    opacity={opacity}
                                    style={{ transition: 'opacity 0.2s ease' }}
                                />
                                
                                {/* Small circles at each data point */}
                                {points.map((point, idx) => (
                                    <circle
                                        key={`${groupKey}-point-${idx}`}
                                        cx={point.x}
                                        cy={point.y}
                                        r="2.5"
                                        fill={color}
                                        opacity={opacity}
                                        style={{ transition: 'opacity 0.2s ease' }}
                                    />
                                ))}
                                
                                {/* Label at end of line */}
                                <line
                                    x1={lastPoint.x}
                                    y1={lastPoint.y}
                                    x2={width - padding.right + 10}
                                    y2={lastPoint.y}
                                    stroke="#666"
                                    strokeWidth="1"
                                    strokeDasharray="2 2"
                                    opacity={opacity}
                                    style={{ transition: 'opacity 0.2s ease' }}
                                />
                                <text
                                    x={width - padding.right + 15}
                                    y={lastPoint.y + 4}
                                    fill={color}
                                    fontSize="12"
                                    fontWeight="400"
                                    opacity={opacity}
                                    style={{ 
                                        cursor: 'pointer',
                                        transition: 'opacity 0.2s ease'
                                    }}
                                    onMouseEnter={() => setHoveredOutcome(groupKey)}
                                    onMouseLeave={() => setHoveredOutcome(null)}
                                >
                                    {getGroupLabel(groupKey)}
                                </text>
                            </g>
                        );
                    })}

                    {/* Hover zones for each month */}
                    {months.map((month, idx) => {
                        const x = getX(idx);
                        
                        // Calculate zone boundaries
                        let zoneX, zoneWidth;
                        
                        if (idx === 0) {
                            const nextX = idx < months.length - 1 ? getX(idx + 1) : width - padding.right;
                            zoneX = padding.left;
                            zoneWidth = (x + nextX) / 2 - padding.left;
                        } else if (idx === months.length - 1) {
                            const prevX = getX(idx - 1);
                            zoneX = (prevX + x) / 2;
                            zoneWidth = (width - padding.right) - zoneX;
                        } else {
                            const prevX = getX(idx - 1);
                            const nextX = getX(idx + 1);
                            zoneX = (prevX + x) / 2;
                            zoneWidth = (x + nextX) / 2 - zoneX;
                        }
                        
                        return (
                            <rect
                                key={`zone-${month}`}
                                x={zoneX}
                                y={0}
                                width={zoneWidth}
                                height={height}
                                fill="transparent"
                                style={{ cursor: 'pointer' }}
                                onMouseEnter={() => setHoveredWindowWithDebounce(month)}
                                onMouseLeave={() => clearHoveredWindowWithDebounce()}
                            />
                        );
                    })}

                    {/* Vertical line and points when hovering */}
                    {hoveredWindow !== null && (() => {
                        const hoveredIdx = months.indexOf(hoveredWindow);
                        if (hoveredIdx === -1) return null;
                        
                        return (
                            <g>
                                {/* Vertical line */}
                                <line
                                    x1={getX(hoveredIdx)}
                                    y1={padding.top}
                                    x2={getX(hoveredIdx)}
                                    y2={height - padding.bottom}
                                    stroke="#888"
                                    strokeWidth="1.5"
                                    strokeDasharray="4 2"
                                />
                                
                                {/* Points for each outcome-weight class at this month */}
                                {outcomeWeightGroups.map((groupKey) => {
                                    const data = chartData[hoveredWindow]?.[groupKey];
                                    if (!data) return null;
                                    
                                    const color = getWeightClassColor(data.outcome_group, data.weight_class);
                                    const x = getX(hoveredIdx);
                                    const y = getY(data.proportion);
                                    
                                    return (
                                        <circle
                                            key={`point-${groupKey}`}
                                            cx={x}
                                            cy={y}
                                            r="5"
                                            fill={color}
                                            stroke="#303030"
                                            strokeWidth="2"
                                        />
                                    );
                                })}
                            </g>
                        );
                    })()}

                    {/* Axes */}
                    <line
                        x1={padding.left}
                        y1={padding.top}
                        x2={padding.left}
                        y2={height - padding.bottom}
                        stroke="#ccc"
                        strokeWidth="2"
                    />
                    <line
                        x1={padding.left}
                        y1={height - padding.bottom}
                        x2={width - padding.right}
                        y2={height - padding.bottom}
                        stroke="#ccc"
                        strokeWidth="2"
                    />

                    {/* Axis labels */}
                    <text
                        x={-height / 2}
                        y={20}
                        textAnchor="middle"
                        fill="#fff"
                        fontSize="14"
                        fontWeight="600"
                        transform={`rotate(-90, ${20}, ${height / 2})`}
                    >
                        Proportion (%)
                    </text>
                    


                    {/* Hover tooltip inside SVG */}
                    {hoveredWindow !== null && (() => {
                        const hoveredIdx = months.indexOf(hoveredWindow);
                        if (hoveredIdx === -1) return null;
                        
                        // Group data by outcome for cleaner display
                        const tooltipByOutcome = {};
                        outcomeWeightGroups.forEach(groupKey => {
                            const data = chartData[hoveredWindow]?.[groupKey];
                            if (!data) return;
                            const outcome = data.outcome_group;
                            if (!tooltipByOutcome[outcome]) {
                                tooltipByOutcome[outcome] = [];
                            }
                            tooltipByOutcome[outcome].push(data);
                        });
                        
                        const tooltipX = getX(hoveredIdx) + 15;
                        const tooltipY = padding.top + 30;
                        const tooltipWidth = 130;
                        
                        // Calculate height based on content
                        let lineCount = 1; // title
                        Object.values(tooltipByOutcome).forEach(dataList => {
                            lineCount += 1; // outcome header
                            lineCount += dataList.length; // each weight class
                        });
                        const tooltipHeight = 30 + (lineCount * 16);
                        
                        // Use cursor position for tooltip, with offset
                        let tooltipXPos = cursorPos.x + 15;
                        let tooltipYPos = cursorPos.y - tooltipHeight - 10;
                        
                        // Keep tooltip within bounds
                        if (tooltipXPos + tooltipWidth > width - padding.right) {
                            tooltipXPos = width - padding.right - tooltipWidth - 10;
                        }
                        if (tooltipYPos < padding.top) {
                            tooltipYPos = cursorPos.y + 15;
                        }
                        
                        return (
                            <g>
                                {/* Tooltip background rectangle */}
                                <rect
                                    x={tooltipXPos}
                                    y={tooltipYPos}
                                    width={tooltipWidth}
                                    height={tooltipHeight}
                                    fill="#1a1a1a"
                                    stroke="#555"
                                    strokeWidth="1"
                                    rx="8"
                                    ry="8"
                                />
                                
                                {/* Tooltip text content */}
                                <text
                                    x={tooltipXPos + 10}
                                    y={tooltipYPos + 18}
                                    fill="#fff"
                                    fontSize="12"
                                    fontWeight="700"
                                >
                                    {hoveredWindow}
                                </text>
                                
                                {(() => {
                                    let currentY = tooltipYPos + 38;
                                    const elements = [];
                                    
                                    Object.entries(tooltipByOutcome).forEach(([outcome, dataList]) => {
                                        // Outcome header without colored square
                                        elements.push(
                                            <text
                                                key={`header-${outcome}`}
                                                x={tooltipXPos + 10}
                                                y={currentY}
                                                fill="#ccc"
                                                fontSize="11"
                                                fontWeight="600"
                                            >
                                                {outcome}
                                            </text>
                                        );
                                        currentY += 14;
                                        
                                        // Sort weight class data by proportion (highest to lowest)
                                        const sortedDataList = [...dataList].sort((a, b) => b.proportion - a.proportion);
                                        
                                        // Weight class data with colored square
                                        sortedDataList.forEach((data, idx) => {
                                            const weightColor = getWeightClassColor(data.outcome_group, data.weight_class);
                                            elements.push(
                                                <g key={`${outcome}-group-${idx}`}>
                                                    <rect
                                                        x={tooltipXPos + 8}
                                                        y={currentY - 8}
                                                        width="6"
                                                        height="6"
                                                        fill={weightColor}
                                                        rx="0.5"
                                                    />
                                                    <text
                                                        key={`${outcome}-${idx}`}
                                                        x={tooltipXPos + 18}
                                                        y={currentY}
                                                        fill="#ccc"
                                                        fontSize="10"
                                                    >
                                                        {data.weight_class}: {Math.round(data.proportion * 100)}% ({data.count}/{data.total_in_window})
                                                    </text>
                                                </g>
                                            );
                                            currentY += 14;
                                        });
                                        
                                        // Add extra spacing between outcomes
                                        currentY += 6;
                                    });
                                    
                                    return elements;
                                })()}
                            </g>
                        );
                    })()}
                </svg>
            </div>

            {/* Fight Outcome Frequencies Table */}
            <div style={styles.frequenciesTableContainer}>
                <h2 style={styles.tableTitle}>Fight Outcome Type Frequencies (all UFC events)</h2>
                <p style={styles.tableInstructions}>Click on any outcome to see its most recent instances</p>
                {frequenciesLoading ? (
                    <div style={styles.loading}>Loading frequencies...</div>
                ) : (
                    <div style={styles.tableWrapper}>
                        <table style={styles.frequenciesTable}>
                            <thead>
                                <tr style={styles.tableHeaderRow}>
                                    <th style={styles.tableHeader}>Fight Outcome Type</th>
                                    <th style={styles.tableHeader}>Frequency</th>
                                </tr>
                            </thead>
                            <tbody>
                                {outcomeFrequencies.map((row, idx) => (
                                    <React.Fragment key={idx}>
                                        <tr 
                                            style={{
                                                ...styles.tableBodyRow,
                                                backgroundColor: expandedOutcome === row.fight_outcome_type ? '#2a2a2a' : 'transparent'
                                            }}
                                            onClick={() => toggleOutcomeExpand(row.fight_outcome_type)}
                                            onMouseEnter={(e) => {
                                                if (expandedOutcome !== row.fight_outcome_type) {
                                                    e.currentTarget.style.backgroundColor = '#3a3a3a';
                                                }
                                                // Show the arrow on hover
                                                const arrow = e.currentTarget.querySelector('span');
                                                if (arrow) arrow.style.opacity = '1';
                                                e.currentTarget.style.cursor = 'pointer';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = expandedOutcome === row.fight_outcome_type ? '#2a2a2a' : 'transparent';
                                                // Hide the arrow when not hovering
                                                const arrow = e.currentTarget.querySelector('span');
                                                if (arrow) arrow.style.opacity = '0';
                                            }}
                                        >
                                            <td style={styles.tableCell}>
                                                <span style={{ opacity: 0, transition: 'opacity 0.2s ease', display: 'inline-block', marginRight: '8px', fontSize: '12px' }}>
                                                    {expandedOutcome === row.fight_outcome_type ? '↓' : '↓'}
                                                </span>
                                                {row.fight_outcome_type}
                                            </td>
                                            <td style={styles.tableCell}>{row.frequency}</td>
                                        </tr>
                                        
                                        {/* Expanded fights list */}
                                        {expandedOutcome === row.fight_outcome_type && (
                                            <tr style={styles.expandedRow}>
                                                <td colSpan="2" style={styles.expandedCell}>
                                                    {expandedFightsLoading ? (
                                                        <div style={styles.expandedLoading}>Loading fights...</div>
                                                    ) : expandedFights.length === 0 ? (
                                                        <div style={styles.expandedNoData}>No fights found</div>
                                                    ) : (
                                                        <div style={styles.fightsList}>
                                                            <table style={styles.fightsTable}>
                                                                <thead>
                                                                    <tr style={styles.fightsTableHeaderRow}>
                                                                        <th style={styles.fightsTableHeader}>Fighter 1</th>
                                                                        <th style={styles.fightsTableHeader}>Fighter 2</th>
                                                                        <th style={styles.fightsTableHeader}>Winner</th>
                                                                        <th style={styles.fightsTableHeader}>Date</th>
                                                                        <th style={styles.fightsTableHeader}>Event</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {expandedFights.map((fight, fightIdx) => (
                                                                        <tr key={fightIdx} style={styles.fightsTableRow}>
                                                                            <td style={styles.fightsTableCell}>{fight.fighter1}</td>
                                                                            <td style={styles.fightsTableCell}>{fight.fighter2}</td>
                                                                            <td style={styles.fightsTableCell}>{fight.fight_winner}</td>
                                                                            <td style={styles.fightsTableCell}>{fight.event_date}</td>
                                                                            <td style={styles.fightsTableCell}>
                                                                                {fight.event_url ? (
                                                                                    <a 
                                                                                        href={fight.event_url} 
                                                                                        target="_blank" 
                                                                                        rel="noopener noreferrer" 
                                                                                        style={styles.eventLink}
                                                                                        onMouseEnter={(e) => e.currentTarget.style.color = '#4FC3F7'}
                                                                                        onMouseLeave={(e) => e.currentTarget.style.color = '#ddd'}
                                                                                    >
                                                                                        {fight.event_name}
                                                                                    </a>
                                                                                ) : (
                                                                                    <span 
                                                                                        style={styles.eventLink}
                                                                                        onMouseEnter={(e) => e.currentTarget.style.color = '#4FC3F7'}
                                                                                        onMouseLeave={(e) => e.currentTarget.style.color = '#ddd'}
                                                                                    >
                                                                                        {fight.event_name}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: {
        padding: '10px 20px',
        maxWidth: '950px',
        margin: '0 auto'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#fff',
        marginBottom: '5px',
        marginTop: '0'
    },
    windowInfo: {
        fontSize: '13px',
        color: '#999',
        marginBottom: '0',
        fontStyle: 'italic'
    },
    subtitle: {
        fontSize: '16px',
        color: '#bbb',
        marginBottom: '30px'
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '20px'
    },
    filtersWrapper: {
        display: 'flex',
        gap: '30px',
        flexWrap: 'wrap'
    },
    filterContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '10px'
    },
    filterLabelWithButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        justifyContent: 'space-between',
        width: '100%'
    },
    filterLabel: {
        color: '#ccc',
        fontSize: '14px',
        fontWeight: '600'
    },
    clearButton: {
        padding: '4px 10px',
        backgroundColor: '#555',
        color: '#ccc',
        border: '1px solid #777',
        borderRadius: '4px',
        fontSize: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    clearButtonPlainText: {
        background: 'none',
        border: 'none',
        color: '#ccc',
        fontSize: '12px',
        cursor: 'pointer',
        padding: '0',
        textDecoration: 'underline',
        transition: 'color 0.2s ease',
    },
    clearButtonHover: {
        backgroundColor: '#666',
        color: '#fff'
    },
    multiSelectContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '10px',
        backgroundColor: 'transparent',
        borderRadius: '4px',
        border: '1px solid #555',
        width: '150px'
    },
    multiSelectContainerHorizontal: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
        gap: '8px',
        padding: '10px',
        backgroundColor: 'transparent',
        borderRadius: '4px',
        border: '1px solid #555',
        minWidth: '300px'
    },
    weightClassGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    weightClassGroupMale: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '8px',
    },
    divider: {
        height: '1px',
        backgroundColor: '#555',
        gridColumn: '1 / -1',
        margin: '4px 0'
    },
    weightClassGroupLabel: {
        color: '#999',
        fontSize: '11px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '4px',
        gridColumn: '1 / -1'
    },
    checkboxLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: '#ccc',
        fontSize: '13px',
        cursor: 'pointer',
        userSelect: 'none'
    },
    checkbox: {
        cursor: 'pointer',
        accentColor: '#4ECDC4'
    },
    outcomeDot: {
        width: '12px',
        height: '12px',
        borderRadius: '2px',
        flexShrink: 0
    },
    filterSelect: {
        padding: '8px 12px',
        backgroundColor: '#333',
        color: '#fff',
        border: '1px solid #555',
        borderRadius: '4px',
        fontSize: '14px',
        cursor: 'pointer',
        minWidth: '180px'
    },
    rankThresholdRow: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
    },
    windowInputGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
    },
    windowInputUnit: {
        color: '#999',
        fontSize: '12px'
    },
    applyButton: {
        padding: '6px 10px',
        backgroundColor: '#4ECDC4',
        color: '#1a1a1a',
        border: 'none',
        borderRadius: '4px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background-color 0.2s ease'
    },
    applyButtonDisabled: {
        backgroundColor: '#555',
        color: '#999',
        cursor: 'default'
    },
    loading: {
        fontSize: '18px',
        color: '#4FC3F7',
        textAlign: 'center',
        padding: '40px'
    },
    error: {
        fontSize: '18px',
        color: '#FF6B6B',
        textAlign: 'center',
        padding: '40px'
    },
    chartContainer: {
        position: 'relative',
        backgroundColor: 'transparent',
        padding: '60px',
        borderRadius: '10px'
    },
    svg: {
        display: 'block'
    },
    tooltip: {
        position: 'absolute',
        backgroundColor: '#2a2a2a',
        border: '2px solid #555',
        borderRadius: '8px',
        padding: '12px 15px',
        minWidth: '140px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none',
        zIndex: 1000,
        maxWidth: '250px'
    },
    tooltipTitle: {
        fontSize: '14px',
        fontWeight: '700',
        color: '#fff',
        marginBottom: '10px',
        paddingBottom: '8px',
        borderBottom: '1px solid #444'
    },
    tooltipRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '6px',
        fontSize: '13px'
    },
    tooltipOutcomeHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginTop: '8px',
        marginBottom: '4px',
        fontSize: '12px',
        fontWeight: '600'
    },
    tooltipSubRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginLeft: '16px',
        marginBottom: '4px',
        fontSize: '12px'
    },
    tooltipWeightClass: {
        color: '#999',
        minWidth: '40px'
    },
    tooltipDot: {
        width: '10px',
        height: '10px',
        borderRadius: '2px',
        flexShrink: 0
    },
    tooltipLabel: {
        color: '#bbb',
        flex: 0,
        whiteSpace: 'nowrap'
    },
    tooltipValue: {
        color: '#fff',
        fontWeight: '600',
        marginLeft: 'auto'
    },
    tooltipCount: {
        color: '#fff',
        fontWeight: '400',
        marginLeft: '4px'
    },
    frequenciesTableContainer: {
        marginTop: '40px',
        marginBottom: '40px'
    },
    tableTitle: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#fff',
        marginBottom: '20px',
        marginTop: '0'
    },
    tableInstructions: {
        fontSize: '13px',
        color: '#999',
        marginBottom: '16px',
        marginTop: '-10px',
        fontStyle: 'italic'
    },
    tableWrapper: {
        overflowX: 'auto',
        borderRadius: '8px',
        border: '1px solid #555',
        display: 'inline-block'
    },
    frequenciesTable: {
        width: 'auto',
        borderCollapse: 'collapse',
        minWidth: '300px'
    },
    tableHeaderRow: {
        backgroundColor: '#2a2a2a'
    },
    tableHeader: {
        padding: '3px 16px',
        textAlign: 'left',
        color: '#ccc',
        fontWeight: '600',
        fontSize: '14px',
        borderBottom: '2px solid #555'
    },
    tableBodyRow: {
        borderBottom: '1px solid #444',
        transition: 'background-color 0.2s ease',
        cursor: 'pointer'
    },
    tableCell: {
        padding: '3px 16px',
        color: '#ddd',
        fontSize: '12px'
    },
    expandedRow: {
        backgroundColor: '#1a1a1a',
        borderBottom: '2px solid #555'
    },
    expandedCell: {
        padding: '4px 16px',
        color: '#ddd',
        fontSize: '11px'
    },
    expandedLoading: {
        textAlign: 'center',
        color: '#4FC3F7',
        padding: '20px'
    },
    expandedNoData: {
        textAlign: 'center',
        color: '#999',
        padding: '20px'
    },
    fightsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
        width: '100%'
    },
    fightsTable: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '12px'
    },
    fightsTableHeaderRow: {
        backgroundColor: '#3a3a3a',
        borderBottom: '1px solid #555'
    },
    fightsTableHeader: {
        padding: '3px 12px',
        textAlign: 'left',
        color: '#ccc',
        fontWeight: '600',
        fontSize: '11px',
        borderRight: '1px solid #444'
    },
    fightsTableRow: {
        borderBottom: '1px solid #333',
        backgroundColor: '#222'
    },
    fightsTableCell: {
        padding: '3px 12px',
        color: '#ccc',
        fontSize: '11px',
        borderRight: '1px solid #333'
    },
    eventLink: {
        color: '#ddd',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'color 0.2s ease'
    }
};

export default FightOutcomeVisualizer;
